import Box from '@mui/material/Box';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { AppBar } from 'components/shell/app-bar';
import Search from 'components/views/search';
import { useQueryParamState } from 'hooks/use-query-params';
import { useAuthContext } from 'providers/auth';
import { PageContext } from 'providers/page';
import { useSnackbar } from 'providers/snackbar';
import { FC, useEffect, useState } from 'react';
import { deleteLocalStorageAuth } from 'utils/local-storage';

interface AppLayoutProps {
  children?: React.ReactNode;
}

export const AppLayout: FC<AppLayoutProps> = ({ children }): JSX.Element => {
  const authContext = useAuthContext();
  const [querySearch, updateSearch, deleteSearch] = useQueryParamState('globalSearchQuery');
  const { openSnackbar } = useSnackbar();
  const [leftNavDrawerOpen, setLeftNavDrawerOpen] = useState(false);

  const searchOpen = typeof querySearch !== 'undefined';

  const toggleLeftNavDrawer = () => {
    setLeftNavDrawerOpen(!leftNavDrawerOpen);
  };

  const handleLogout = async () => {
    try {
      deleteLocalStorageAuth();
      await signOut();
    } catch (e) {
      console.log('error logging out', e);
    }
  };

  const openSearch = () => {
    updateSearch('', false);
  };

  const closeSearch = () => {
    deleteSearch(false);
  };

  const searchChange = (search: string) => {
    updateSearch(search);
  };

  const copyAuthToken = async () => {
    const session = await fetchAuthSession();
    await navigator.clipboard.writeText(session.tokens?.accessToken?.toString() || '');
    openSnackbar({ message: 'Auth token copied to clipboard', severity: 'success' });
  };

  const handleKeypress = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.code === 'KeyK') {
      openSearch();
    } else if ((e.metaKey || e.ctrlKey) && e.code === 'KeyJ') {
      copyAuthToken();
    }
  };

  useEffect(() => {
    if (!authContext.isInternal) {
      return;
    }
    document.addEventListener('keydown', handleKeypress);
    return () => document.removeEventListener('keydown', handleKeypress);
  }, []);

  return (
    <Box>
      <Box flexGrow={1}>
        <PageContext.Provider
          value={{
            openSearch,
            leftNavDrawerOpen,
            toggleLeftNavDrawer,
          }}
        >
          <AppBar
            handleDrawerToggle={toggleLeftNavDrawer}
            handleSearchToggle={searchOpen ? closeSearch : openSearch}
            handleLogout={handleLogout}
          />
          {children}
        </PageContext.Provider>
      </Box>

      {authContext.isInternal && (
        <Search
          open={searchOpen}
          closeSearch={closeSearch}
          initialSearch={querySearch || ''}
          onSearchChange={searchChange}
        />
      )}
    </Box>
  );
};
